// ─── Export Data ─────────────────────────────────────────────────────────────
//
// The legacy reports screen puts an "Export Data" button on every table, and a
// reader's next move after reading a report is nearly always to put it in a
// spreadsheet. This builds the file; the component hands it to the browser.
//
// Pure, so the escaping rules are testable — they are the whole risk here. A
// procedure template called `Thaw, Wash` or a name carrying a quote will
// silently shift every column after it if the quoting is wrong, and nobody
// notices until a figure is read out of the wrong column.

/** A cell as it comes out of a report: already formatted, or empty. */
export type CsvValue = string | number | null | undefined

/**
 * RFC 4180 escaping.
 *
 * A field is quoted when it contains a comma, a quote, or a newline, and inner
 * quotes are doubled. Everything else goes through untouched, which keeps the
 * common case readable in a diff.
 */
export function escapeCsvCell(value: CsvValue): string {
  if (value === null || value === undefined) return ''
  const text = String(value)
  if (!/[",\n\r]/.test(text)) return text
  return `"${text.replace(/"/g, '""')}"`
}

/**
 * A CSV document from a header row and body rows.
 *
 * CRLF line endings, because that is what RFC 4180 says and what Excel expects;
 * every other reader accepts them.
 */
export function toCsv(headers: string[], rows: CsvValue[][]): string {
  return [headers, ...rows].map((row) => row.map(escapeCsvCell).join(',')).join('\r\n')
}

/**
 * A filename that says what the file is without being opened.
 *
 * `procedure-volume_2026-08-10_2026-09-08_daily.csv` — the report, the window
 * it covers and the grain, so two exports taken a week apart never collide in
 * a downloads folder.
 */
export function reportFileName(
  slug: string,
  window: { from: string; to: string },
  suffix?: string,
): string {
  return [slug, window.from, window.to, suffix].filter(Boolean).join('_') + '.csv'
}
